import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, Modal, Field, inputCls, PrimaryButton } from "@/components/lims";
import { Plus, PencilSimple, Trash, ArrowSquareOut, LockSimple } from "@phosphor-icons/react";
import { canManageCatalogue } from "@/lib/roles";

const empty = { name: "", description: "", active: true };

export default function Configuration() {
  const { user } = useAuth();
  const canManage = canManageCatalogue(user);
  const [types, setTypes] = useState([]);
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [t, l] = await Promise.all([api.get("/sample-types"), api.get("/config/lifecycle")]);
    setTypes(t.data);
    setLifecycle(l.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, description: t.description || "", active: t.active ?? true }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("A name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sample-types/${editing.id}`, form);
        toast.success("Sample type updated");
      } else {
        await api.post("/sample-types", form);
        toast.success("Sample type added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!(await voidRecord(`/sample-types/${t.id}`, `sample type ${t.name}`))) return;
    toast.success("Sample type voided");
    load();
  };

  return (
    <Page>
      <PageHeader
        title="Configuration"
        subtitle="Laboratory vocabulary you can change without a deployment. Every change is audited."
        action={canManage ? <PrimaryButton data-testid="new-sample-type-button" onClick={openNew}><Plus size={18} /> Sample type</PrimaryButton> : null}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <section aria-label="Sample types">
          <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Sample types</h2>
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
            {loading ? (
              <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Description</th>
                    <th className="px-4 py-3 font-semibold">Active</th>
                    {canManage && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {types.map((t) => (
                    <tr key={t.id} data-testid={`sample-type-row-${t.name}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-slate-900">{t.name}</td>
                      <td className="px-4 py-3 text-slate-500">{t.description || "—"}</td>
                      <td className="px-4 py-3">{t.active ?? true ? <span className="text-status-ok text-xs font-medium">Active</span> : <span className="text-slate-400 text-xs">Inactive</span>}</td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button data-testid={`edit-type-${t.name}`} onClick={() => openEdit(t)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><PencilSimple size={16} /></button>
                            <button data-testid={`void-type-${t.name}`} onClick={() => remove(t)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
            <span className="font-medium text-slate-900">Test catalog &amp; TAT targets</span>: assays,
            specifications, and turnaround-day targets are managed in the{" "}
            <Link to="/catalogue" className="inline-flex items-center gap-1 text-slate-900 underline hover:no-underline">
              Test Catalog <ArrowSquareOut size={14} />
            </Link>.
          </div>
        </section>

        <section aria-label="Lifecycle reference">
          <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
            <LockSimple size={14} /> Lifecycle (read-only, DEC-009)
          </h2>
          <div className="bg-white rounded-lg border border-slate-200 p-4 text-sm">
            <p className="text-slate-500 text-xs mb-3">
              Statuses and transitions are validation-controlled and change through
              reviewed code, not configuration. An editable state machine would
              undermine the FR-04 guarantees.
            </p>
            {lifecycle && (
              <>
                <div className="mb-3">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Sample statuses</span>
                  <div className="mt-1 text-slate-700">{lifecycle.sample_statuses.join(" → ")}</div>
                </div>
                <div className="mb-3">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Test statuses</span>
                  <div className="mt-1 text-slate-700">{lifecycle.test_statuses.join(" → ")}</div>
                </div>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Transitions</span>
                <table className="w-full text-xs mt-1">
                  <tbody>
                    {lifecycle.transitions.map((t) => (
                      <tr key={t.action} className="border-b border-slate-100 last:border-0">
                        <td className="py-1.5 pr-2 font-medium text-slate-900">{t.action}</td>
                        <td className="py-1.5 pr-2 text-slate-500">{t.from.join(", ")} → {t.to}</td>
                        <td className="py-1.5 text-slate-400">{t.reason_required ? "reason required" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit ${editing.name}` : "New sample type"} testid="sample-type-modal">
        <form onSubmit={submit} noValidate className="space-y-4">
          <Field label="Name"><input data-testid="sample-type-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="rounded border-slate-300" />
            Active (selectable at registration)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-sample-type-button" disabled={saving}>{editing ? "Save" : "Add"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </Page>
  );
}
